/**
 * DealCharts — the analytical half of the deal page.
 *
 * The tables above these panels say what is true right now. These say how it
 * got that way and where it goes: what the building has signed over a decade
 * against what the submarket signed at the same time, how occupancy runs off
 * if nothing renews, what rolls when, what we underwrote against what the
 * property actually spends, and where this sits in the expense geography
 * around it.
 *
 * Every panel carries its own "how this is computed" and every panel is
 * willing to say it has nothing. A chart that fills an empty quarter with a
 * straight line is lying quietly, which is worse than an empty panel.
 */

import { useMemo, useState } from "react";
import * as B from "../common/brand";
import { TimeSeries, Scatter, Columns, HeatGrid, Legend, Explain } from "../common/Charts";
import {
  signedRentHistory, occupancyCurve, rolloverProfile,
  opexCompare, areaOpexHeat, rentBySize,
} from "../../services/dealAnalytics";

const psf = (v) => (Number.isFinite(v) && v > 0 ? `$${Number(v).toFixed(2)}` : "—");
// A line worth a few hundred dollars across a big building rounds to $0.00,
// which reads as "free" rather than "too small to show at this precision".
const psfTiny = (v) => (Number.isFinite(v) && v > 0 && v < 0.005 ? "<$0.01" : psf(v));
const psf0 = (v) => (Number.isFinite(v) ? `$${Number(v).toFixed(v < 10 ? 2 : 0)}` : "—");
const pct1 = (v) => (Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : "—");
const pct0 = (v) => (Number.isFinite(v) ? `${Math.round(v * 100)}%` : "—");
const sf0 = (v) => (Number.isFinite(Number(v)) && Number(v) ? Math.round(Number(v)).toLocaleString() : "—");
const money = (v) => (Number.isFinite(v) && v ? `$${Math.round(v).toLocaleString()}` : "—");

const yearFmt = (t) => String(new Date(t).getUTCFullYear());
const qFmt = (t) => {
  const d = new Date(t);
  return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${String(d.getUTCFullYear()).slice(2)}`;
};
const monthFmt = (t) => {
  const d = new Date(t);
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
};

/* ── panel shell ──────────────────────────────────────────────────── */
function Panel({ title, sub, how, right, children, className = "" }) {
  return (
    <section className={`${B.CARD} ${className}`}>
      <header className={B.CARD_HEAD}>
        <div className="min-w-0">
          <h3 className={B.TITLE}>{title}</h3>
          {sub && <p className={`${B.SUBTITLE} mt-0.5`}>{sub}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {right}
          {how && <Explain>{how}</Explain>}
        </div>
      </header>
      {children}
    </section>
  );
}

function Stat({ label, value, sub, tone = "bright" }) {
  const tones = { bright: "text-[#f4f4f4]", up: "text-[#4f9d76]", down: "text-[#e93529]", warn: "text-[#c8922f]", muted: "text-[#8a8a8a]" };
  return (
    <div className="px-5 py-3 flex-1 min-w-[152px]">
      <div className={B.LABEL}>{label}</div>
      <div className={`mt-1 text-[19px] leading-none font-light tracking-tight tabular-nums ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-1.5 text-[10.5px] text-[#8a8a8a] leading-snug">{sub}</div>}
    </div>
  );
}

/* ── 1. signed rents over time ────────────────────────────────────── */
function SignedRents({ rentRoll, comps, radius }) {
  const [view, setView] = useState("trend");
  const h = useMemo(() => signedRentHistory(rentRoll, comps), [rentRoll, comps]);
  const xFmt = h.granularity === "year" ? yearFmt : qFmt;

  const toggle = (
    <div className="flex items-center gap-1">
      {[["trend", "Averages"], ["each", "Every lease"]].map(([k, label]) => (
        <button key={k} onClick={() => setView(k)}
          className={`px-2 py-1 rounded-lg text-[11px] transition ${view === k
            ? "bg-[#2c2c2c] text-[#f4f4f4]" : "text-[#8a8a8a] hover:text-[#b4b4b4]"}`}>
          {label}
        </button>
      ))}
    </div>
  );

  if (h.empty) {
    return (
      <Panel title="Signed rents over time" sub="what this building signed, and when" right={toggle} how={h.how}>
        <div className="px-5 py-10 text-[12.5px] text-[#8a8a8a] leading-relaxed">
          No lease at this property carries both a commencement date and a rate,
          and no dated comp is in range — so there is no history to draw. The rent
          roll may still be complete; it is the dates that are missing.
        </div>
      </Panel>
    );
  }

  const spread = h.latest.spread;
  const growth = h.propTrend?.pctPerYear;
  const mktGrowth = h.mktTrend?.pctPerYear;

  return (
    <Panel
      title="Signed rents over time"
      sub={`${h.counts.property} signing${h.counts.property === 1 ? "" : "s"} here against ${h.counts.market} dated comps within ${radius} mi`}
      right={toggle} how={h.how} className="xl:col-span-2">

      <div className="flex flex-wrap divide-x divide-[#303030] border-b border-[#3a3a3a]">
        <Stat label="Last signed here" value={psf(h.latest.property)}
              sub={h.granularity === "year" ? "weighted, most recent year with a signing" : "weighted, most recent quarter"} />
        <Stat label="Submarket, same period" value={psf(h.latest.market)} sub="area-weighted comps in the radius" />
        <Stat label="Spread to market" value={spread == null ? "—" : `${spread > 0 ? "+" : ""}${pct1(spread)}`}
              tone={spread == null ? "muted" : spread > 0.05 ? "up" : spread < -0.05 ? "down" : "bright"}
              sub={spread == null ? "one side is missing" : spread > 0 ? "this building signs above the submarket" : "this building signs below the submarket"} />
        <Stat label="Our growth / market" value={growth == null ? "—" : `${growth > 0 ? "+" : ""}${pct1(growth)}`}
              tone={growth == null ? "muted" : growth > 0 ? "up" : "down"}
              sub={mktGrowth == null ? "a year, fitted through every signing" : `a year here against ${mktGrowth > 0 ? "+" : ""}${pct1(mktGrowth)} in the submarket`} />
      </div>

      <div className="px-3 pt-4 pb-1">
        {view === "trend" ? (
          <TimeSeries
            height={240} yFmt={psf0} xFmt={xFmt}
            markers={[{ x: Date.now(), label: "today" }]}
            band={{ points: h.band, color: "rgba(244,244,244,0.05)" }}
            series={[
              { key: "mkt", label: "Submarket", color: "#8a8a8a", points: h.market, dashed: true },
              { key: "prop", label: "This property", color: B.red.DEFAULT, points: h.property, dots: true, width: 2 },
            ]}
            empty="No period holds a signing." />
        ) : (
          <Scatter
            height={240} yFmt={psf0} xFmt={xFmt}
            points={h.scatter} trend={h.propTrend?.points}
            markers={[{ x: Date.now(), label: "today" }]}
            refLines={[
              h.latest.market ? { y: h.latest.market, label: "submarket, latest", color: "#8a8a8a" } : null,
            ].filter(Boolean)}
            empty="Nothing dated to plot." />
        )}
      </div>

      <div className="px-5 pb-4 flex items-center justify-between gap-3 flex-wrap">
        <Legend items={[
          { label: "This property", color: B.red.DEFAULT, shape: view === "each" ? "dot" : "line" },
          { label: "Submarket comps", color: "#8a8a8a", dashed: view === "trend", shape: view === "each" ? "dot" : "line" },
          view === "trend" ? { label: "Comp middle 50%", color: "rgba(244,244,244,0.25)" } : { label: "Fitted trend, this property", color: "#f4f4f4", dashed: true },
        ]} />
        <span className="text-[10.5px] text-[#7a7a7a]">
          {view === "each" ? "dot size is suite area · " : ""}
          anything right of the dashed rule has not commenced yet
        </span>
      </div>
    </Panel>
  );
}

/* ── 2. occupancy and run-off ─────────────────────────────────────── */
function Occupancy({ rentRoll, totalRsf }) {
  const o = useMemo(() => occupancyCurve(rentRoll, totalRsf), [rentRoll, totalRsf]);
  if (o.empty) {
    return <Panel title="Occupancy and run-off" how={o.how}>
      <div className="px-5 py-10 text-[12.5px] text-[#8a8a8a]">No lease carries both a start date and an area.</div>
    </Panel>;
  }
  const fmtY = o.asPct ? pct0 : (v) => `${Math.round(v / 1000)}k`;
  const now = Date.now();
  return (
    <Panel title="Occupancy and run-off"
           sub="built from lease dates — nothing after today assumes a renewal" how={o.how}>
      <div className="flex flex-wrap divide-x divide-[#303030] border-b border-[#3a3a3a]">
        <Stat label="Today" value={o.asPct ? pct1(o.today) : sf0(o.today)}
              tone={o.asPct && o.today < 0.85 ? "warn" : "bright"} sub="occupied on contract" />
        <Stat label="Floor if nothing renews" value={o.trough ? (o.asPct ? pct1(o.trough.y) : sf0(o.trough.y)) : "—"}
              tone={o.trough && o.asPct && o.trough.y < 0.6 ? "down" : "warn"}
              sub={o.trough ? `by ${monthFmt(o.trough.x)}` : "no lease ends inside the window"} />
        <Stat label="At risk" value={o.trough && o.asPct ? pct1(Math.max(0, o.today - o.trough.y)) : "—"}
              sub="the gap between the two, and the case for early renewals" tone="warn" />
      </div>
      <div className="px-3 pt-4 pb-4">
        <TimeSeries
          height={200} yFmt={fmtY} xFmt={monthFmt} yZero
          markers={[{ x: now, label: "today" }]}
          series={[{
            key: "occ", label: "Occupied", color: B.ink.bright, points: o.points,
            area: "rgba(244,244,244,0.06)", width: 1.8,
          }]} />
      </div>
    </Panel>
  );
}

/* ── 3. rollover ──────────────────────────────────────────────────── */
function Rollover({ rentRoll, totalRsf }) {
  const r = useMemo(() => rolloverProfile(rentRoll, totalRsf), [rentRoll, totalRsf]);
  if (r.empty) {
    return <Panel title="Rollover profile" how={r.how}>
      <div className="px-5 py-10 text-[12.5px] text-[#8a8a8a]">No lease end dates are held.</div>
    </Panel>;
  }
  const worst = r.rows.reduce((a, x) => (x.sf > a.sf ? x : a), r.rows[0]);
  return (
    <Panel title="Rollover profile"
           sub={`when the mark to market can actually be captured${r.beyond ? ` · ${r.beyond} lease${r.beyond === 1 ? " runs" : "s run"} beyond the window` : ""}`}
           how={r.how}>
      <div className="px-3 pt-4">
        <Columns
          height={190} data={r.rows.map((x) => ({ label: String(x.year).slice(2), value: x.sf, accent: x.year === worst.year }))}
          cumulative={totalRsf ? r.rows.map((x) => x.cumShare * totalRsf) : null}
          yFmt={(v) => `${Math.round(v / 1000)}k`} />
      </div>
      <div className="px-5 pb-4 pt-1 flex items-baseline justify-between gap-3 flex-wrap">
        <span className="text-[11px] text-[#8a8a8a]">
          Heaviest year is <span className="text-[#f04f44]">{worst.year}</span> at {sf0(worst.sf)} SF
          {worst.share ? ` (${pct1(worst.share)} of the building)` : ""}
          {worst.rent ? `, in place at ${psf(worst.rent)}` : ""}.
        </span>
        <Legend items={[
          { label: "Expiring area", color: "rgba(244,244,244,0.20)" },
          { label: "Cumulative rolled", color: "#f4f4f4", dashed: true },
        ]} />
      </div>
    </Panel>
  );
}

/* ── 4. in place against underwritten ─────────────────────────────── */
function OpexVariance({ opex, totalRsf }) {
  const o = useMemo(() => opexCompare(opex, totalRsf), [opex, totalRsf]);
  if (o.empty) {
    return <Panel title="In place against underwritten" how={o.how}>
      <div className="px-5 py-10 text-[12.5px] text-[#8a8a8a] leading-relaxed">
        No expense line reconciled to a stated total, so none was loaded. A
        figure we cannot tie is one we do not have.
      </div>
    </Panel>;
  }
  const v = o.variance;
  return (
    <Panel
      title={!v ? "Operating expenses"
        : v.kind === "actual" ? `In place against underwritten`
        : v.kind === "broker" ? `Underwritten against the broker's pro forma`
        : `Underwritten against the file's own schedule`}
      sub={v ? `${o.meaning[v.uwBasis] || "our figure"} against ${o.meaning[v.inPlaceBasis] || "the other basis held"}`
             : `${o.bases.length} basis held: ${o.bases.join(", ")}`}
      how={o.how} className="xl:col-span-2">

      {v && (
        <div className="flex flex-wrap divide-x divide-[#303030] border-b border-[#3a3a3a]">
          <Stat label={v.inPlaceBasis} value={psf(v.inPlacePsf)} sub={o.meaning[v.inPlaceBasis]} />
          <Stat label={v.uwBasis} value={psf(v.uwPsf)} sub={o.meaning[v.uwBasis]} />
          <Stat label="We underwrote" value={v.deltaPct == null ? "—" : `${v.deltaPct > 0 ? "+" : ""}${pct1(v.deltaPct)}`}
                tone={v.deltaPct == null ? "muted" : v.deltaPct >= 0 ? "up" : "down"}
                sub={v.deltaPct == null ? "one side is missing"
                  : `${v.deltaPct >= 0 ? "more" : "LESS"} expense than ${
                      v.kind === "actual" ? "the property spends"
                        : v.kind === "broker" ? "the broker's pro forma shows"
                        : "the file's own schedule shows"}${
                      v.deltaPct >= 0 ? " — the conservative direction" : ""}`} />
          <Stat label="Lines we did not carry" value={v.missedCount ? String(v.missedCount) : "none"}
                tone={v.missedCount ? "warn" : "bright"}
                sub={v.missedCount ? `categories in the ${v.inPlaceBasis} that our underwriting has no line for` : `every ${v.inPlaceBasis} line has an underwritten counterpart`} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-5 py-4">
        <div>
          <div className={`${B.LABEL} mb-2`}>Every category, every basis {o.perSf ? "($/SF)" : "(annual)"}</div>
          <HeatGrid
            rows={o.categories} cols={o.bases} cells={o.cells}
            fmt={(x) => (o.perSf
              // A line of a few hundred dollars over a big building rounds to
              // $0.00, which reads as "this costs nothing" rather than "this is
              // too small to show at this precision".
              ? (x > 0 && x < 0.005 ? "<$0.01" : `$${x.toFixed(2)}`)
              : `${Math.round(x / 1000)}k`)}
            rowLabelWidth={120}
            empty="Nothing to shade." />
          <p className="mt-2 text-[10.5px] text-[#7a7a7a] leading-relaxed">
            Shaded by size within the panel. An empty cell means that basis has
            no line for that category — not that the category costs nothing.
          </p>
        </div>

        {v ? (
          <div>
            <div className={`${B.LABEL} mb-2`}>Where the two disagree</div>
            <div className="max-h-[17rem] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#242424]">
                  <tr className="text-left border-b border-[#3a3a3a]">
                    <th className={B.TH}>Category</th>
                    <th className={`${B.TH} text-right`}>{v.inPlaceBasis}</th>
                    <th className={`${B.TH} text-right`}>{v.uwBasis}</th>
                    <th className={`${B.TH} text-right`}>Δ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#303030]">
                  {v.rows.map((r) => (
                    <tr key={r.category} className="hover:bg-[#2c2c2c]">
                      <td className={B.TD}>
                        {r.category}
                        {r.onlyIn && (
                          <span className="ml-2 text-[9.5px] px-1.5 py-0.5 rounded bg-[rgba(200,146,47,0.12)] text-[#d6a955]">
                            only in {r.onlyIn}
                          </span>
                        )}
                      </td>
                      <td className={`${B.TD} text-right tabular-nums`}>{o.perSf ? psfTiny(r.inPlacePsf) : money(r.inPlace)}</td>
                      <td className={`${B.TD} text-right tabular-nums`}>{o.perSf ? psfTiny(r.uwPsf) : money(r.uw)}</td>
                      <td className={`${B.TD} text-right tabular-nums ${
                        r.deltaPct == null ? "text-[#7a7a7a]"
                          : r.deltaPct > 0.1 ? "text-[#4f9d76]"
                          : r.deltaPct < -0.1 ? "text-[#e93529]" : "text-[#8a8a8a]"}`}>
                        {r.deltaPct == null ? "—" : `${r.deltaPct > 0 ? "+" : ""}${pct0(r.deltaPct)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[10.5px] text-[#7a7a7a] leading-relaxed">
              Green is underwriting above the other basis — the safe direction.
              Red is underwriting below it, which is the line to argue about
              before closing.{v.kind !== "actual" && (
                <> This deal holds no trailing-twelve actual, so the comparison
                is against {v.kind === "broker" ? "the broker's pro forma" : "a supporting schedule"},
                which is a weaker thing to be right about.</>
              )}
            </p>
          </div>
        ) : (
          <div className="text-[12px] text-[#8a8a8a] leading-relaxed">
            Only one expense basis is held for this deal, so there is nothing to
            read against it. A comparison needs our own schedule and something
            else — a T12, a year to date, the broker's pro forma or a supporting
            schedule from the file. Where only one exists we show the one rather
            than manufacturing a comparison.
          </div>
        )}
      </div>
    </Panel>
  );
}

/* ── 5. expenses around the property ──────────────────────────────── */
function AreaOpex({ deal, comps, radius, subjectOpexPsf }) {
  const [mode, setMode] = useState("location");
  const a = useMemo(() => areaOpexHeat(deal, comps, radius), [deal, comps, radius]);

  const toggle = (
    <div className="flex items-center gap-1">
      {[["location", "By location"], ["structure", "By distance & size"]].map(([k, label]) => (
        <button key={k} onClick={() => setMode(k)}
          className={`px-2 py-1 rounded-lg text-[11px] transition ${mode === k
            ? "bg-[#2c2c2c] text-[#f4f4f4]" : "text-[#8a8a8a] hover:text-[#b4b4b4]"}`}>{label}</button>
      ))}
    </div>
  );

  if (a.empty) {
    return (
      <Panel title="Expenses around here" sub={`within ${radius} mi`} right={toggle} how={a.how}>
        <div className="px-5 py-10 text-[12.5px] text-[#8a8a8a] leading-relaxed">
          Nothing within {radius} miles carries a stated operating expense figure.
          Expenses travel with the deal file rather than the comp, so this fills
          in as more underwritings are loaded in this submarket.
        </div>
      </Panel>
    );
  }

  const grid = mode === "location" ? a.location : a.structure;
  const p = subjectOpexPsf ? a.pctile(subjectOpexPsf) : null;

  return (
    <Panel title="Expenses around here"
           sub={`${a.stats.n} propert${a.stats.n === 1 ? "y" : "ies"} within ${radius} mi carrying a stated expense figure`}
           right={toggle} how={a.how}>
      <div className="flex flex-wrap divide-x divide-[#303030] border-b border-[#3a3a3a]">
        <Stat label="Area median" value={psf(a.stats.median)} sub={`${psf(a.stats.p25)}–${psf(a.stats.p75)} across the middle half`} />
        <Stat label="This property" value={psf(subjectOpexPsf)}
              tone={p == null ? "muted" : p > 0.75 ? "down" : p < 0.25 ? "up" : "bright"}
              sub={p == null ? "no reconciled expense total held here"
                : `dearer than ${pct0(p)} of what is around it`} />
        <Stat label="Full range" value={`${psf(a.stats.lo)}–${psf(a.stats.hi)}`} sub="every figure in the radius" tone="muted" />
      </div>
      <div className="px-5 py-4">
        <HeatGrid
          rows={grid.rows} cols={grid.cols} cells={grid.cells}
          fmt={(v) => `$${v.toFixed(2)}`} rowLabelWidth={mode === "location" ? 108 : 70}
          subjectCell={mode === "location" ? { row: grid.rows[1], col: grid.cols[1] } : null}
          empty="No cell holds evidence." />
        <p className="mt-3 text-[10.5px] text-[#7a7a7a] leading-relaxed">
          Darker is dearer. {mode === "location"
            ? "The outlined cell is the subject's own block; the grid is a third of the radius per band, north at the top and east on the right."
            : "Rings are distance from the subject, columns are building size — the pair that usually explains an expense gap better than geography does."}
        </p>
      </div>
    </Panel>
  );
}

/* ── 6. rent by suite size ────────────────────────────────────────── */
function RentBySize({ comps, deal }) {
  const r = useMemo(() => rentBySize(comps, deal), [comps, deal]);
  if (r.empty) {
    return <Panel title="Rent by suite size" how={r.how}>
      <div className="px-5 py-10 text-[12.5px] text-[#8a8a8a]">Too few comps carry both an area and a rate.</div>
    </Panel>;
  }
  const idx = r.rows.findIndex((x) => x.label === r.subjectBand);
  return (
    <Panel title="Rent by suite size" sub="small suites rent dearer — worth knowing before calling a comp wrong" how={r.how}>
      <div className="px-3 pt-4">
        <Columns height={175} accentIndex={idx}
          data={r.rows.map((x) => ({
            label: `${x.label} · ${x.n}`, value: x.value,
            // Thin evidence is drawn thin. Three comps is a hint, not a level.
            color: x.n < 3 ? "rgba(244,244,244,0.09)" : "rgba(244,244,244,0.20)",
          }))} yFmt={psf0} />
      </div>
      <div className="px-5 pb-4 text-[11px] text-[#8a8a8a]">
        {!r.subjectBand
          ? "No building area is held, so the subject's own band is unknown."
          : idx < 0
            ? <>This property sits in the <span className="text-[#f04f44]">{r.subjectBand}</span> band, which nothing in the radius matches — no comp here is that size.</>
            : <>This property sits in the <span className="text-[#f04f44]">{r.subjectBand}</span> band.</>}
        {" "}Bars are area-weighted within each band and labelled with the number of
        comps behind them; bands standing on fewer than three are drawn faint.
      </div>
    </Panel>
  );
}

/* ── the section ──────────────────────────────────────────────────── */
export default function DealCharts({
  deal, rentRoll, marketRent, opex, nearby, opexNearby, totalRsf, radius, opexRadius = 5,
  subjectOpexPsf = null,
}) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <SignedRents rentRoll={rentRoll} comps={nearby} radius={radius} />
      <Occupancy rentRoll={rentRoll} totalRsf={totalRsf} />
      <Rollover rentRoll={rentRoll} totalRsf={totalRsf} />
      <OpexVariance opex={opex} totalRsf={totalRsf} />
      <AreaOpex deal={deal} comps={opexNearby} radius={opexRadius} subjectOpexPsf={subjectOpexPsf} />
      <RentBySize comps={nearby} deal={deal} />
    </div>
  );
}
